import React, { useMemo, useState } from "react";
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import CommCard from "src/common/CommCard";
import { CommonPagination } from "src/common/CommonPagination";
import { useApiQuery } from "src/hooks/useApiQuery";
import { TableLoader, TableNotFound } from "src/Utils/TableLoader";
import type { IB2BSession } from "src/Types/module/b2bSession/b2bSession.types";
import B2BSessionTableRow from "./B2BSessionTableRow";
import CreateB2BSessionModal from "./CreateB2BSessionModal";

const B2BSessionList: React.FC = () => {
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [pagination, setPagination] = useState({
		limit: 50,
		skip: 0,
	});
	const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);

	const { data: rawSessions = [], isLoading } = useApiQuery<IB2BSession[]>({
		endPoint: "/api/fair/admin/sessions",
		queryKeys: ["B2BSessions"],
	});

	const sessions = Array.isArray(rawSessions) ? rawSessions : [];

	// Search filter
	const filteredSessions = useMemo(() => {
		if (!searchQuery.trim()) return sessions;
		const query = searchQuery.toLowerCase().trim();
		return sessions.filter(
			(s) =>
				s.session_no?.toLowerCase().includes(query) ||
				s.date?.toLowerCase().includes(query) ||
				String(s.id).includes(query)
		);
	}, [sessions, searchQuery]);

	// Paginated slice
	const paginatedData = useMemo(() => {
		return filteredSessions.slice(
			pagination.skip,
			pagination.skip + pagination.limit
		);
	}, [filteredSessions, pagination.skip, pagination.limit]);

	return (
		<CommCard
			showCardHeader
			headerTitle={`B2B Session List (${filteredSessions.length})`}
			helmetTitle="BTTF - B2B Session List"
			showSearch
			searchPlaceHolder="Search by session name..."
			handleSearchFun={(query) => {
				setSearchQuery(query);
				setPagination((prev) => ({ ...prev, skip: 0 }));
			}}
			showButton
			btnText="Create Session"
			handleBtnForModalFun={() => setOpenCreateModal(true)}
		>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>SL.</TableCell>
							<TableCell>Session Name</TableCell>
							<TableCell>Date</TableCell>
							<TableCell>Time Slot</TableCell>
							<TableCell>Fair ID</TableCell>
							<TableCell>Created At</TableCell>
							<TableCell align="right">Action</TableCell>
						</TableRow>
					</TableHead>

					{isLoading ? (
						<TableLoader />
					) : paginatedData.length > 0 ? (
						<TableBody>
							{paginatedData.map((session, index) => (
								<B2BSessionTableRow
									key={session.id}
									session={session}
									serial={pagination.skip + index + 1}
								/>
							))}
						</TableBody>
					) : (
						<TableNotFound />
					)}
				</Table>
			</TableContainer>

			{filteredSessions.length > 0 && (
				<Box p={2}>
					<CommonPagination
						total={filteredSessions.length}
						dataShowPerPage={pagination.limit}
						paginationCalFun={(limit: number, skip: number) => {
							setPagination({ limit, skip });
						}}
					/>
				</Box>
			)}

			{/* Create Session Modal */}
			<CreateB2BSessionModal
				open={openCreateModal}
				handleClose={() => setOpenCreateModal(false)}
			/>
		</CommCard>
	);
};

export default B2BSessionList;
